import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useNotes } from '@/context/NotesContext';
import { logger } from '@/utils/logger';

// Character limits
const MAX_TOOL_NAME_LENGTH = 128;
const MAX_TOOL_DESCRIPTION_LENGTH = 256;
const MAX_TOOL_BODY_LENGTH = 15000;

const NoteEditorModal: React.FC = () => {
  const {
    isEditorModalOpen,
    setIsEditorModalOpen,
    currentEditingNote,
    addNote,
    updateNote,
    deleteNote,
  } = useNotes();

  const [formData, setFormData] = useState({
    toolName: '',
    toolDescription: '',
    toolBody: '',
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update form data when editing note changes
  useEffect(() => {
    if (currentEditingNote) {
      setFormData({
        toolName: currentEditingNote.toolName,
        toolDescription: currentEditingNote.toolDescription,
        toolBody: currentEditingNote.toolBody,
        isActive: currentEditingNote.isActive,
      });
    } else {
      setFormData({
        toolName: '',
        toolDescription: '',
        toolBody: '',
        isActive: true,
      });
    }
  }, [currentEditingNote, isEditorModalOpen]);

  const handleClose = () => {
    setIsEditorModalOpen(false);
    setFormData({
      toolName: '',
      toolDescription: '',
      toolBody: '',
      isActive: true,
    });
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!formData.toolName.trim() || !formData.toolBody.trim()) return;

    // Validate tool name pattern (alphanumeric, underscore, hyphen only, 1-128 chars)
    const toolNamePattern = /^[a-zA-Z0-9_-]{1,128}$/;
    if (!toolNamePattern.test(formData.toolName.trim())) {
      setErrorMessage(
        'Tool name must contain only letters, numbers, underscores, and hyphens (1-128 characters)'
      );
      return;
    }

    // Validate field lengths
    if (formData.toolName.length > MAX_TOOL_NAME_LENGTH) {
      setErrorMessage(
        `Tool name must be ${MAX_TOOL_NAME_LENGTH} characters or less`
      );
      return;
    }
    if (formData.toolDescription.length > MAX_TOOL_DESCRIPTION_LENGTH) {
      setErrorMessage(
        `Description must be ${MAX_TOOL_DESCRIPTION_LENGTH} characters or less`
      );
      return;
    }
    if (formData.toolBody.length > MAX_TOOL_BODY_LENGTH) {
      setErrorMessage(
        `Content must be ${MAX_TOOL_BODY_LENGTH} characters or less`
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const noteData = {
        toolName: formData.toolName.trim(),
        toolDescription: formData.toolDescription.trim(),
        toolBody: formData.toolBody.trim(),
        isActive: formData.isActive,
      };

      if (currentEditingNote) {
        await updateNote(currentEditingNote.id, noteData);
      } else {
        await addNote(noteData);
      }

      handleClose();
    } catch (error) {
      logger.error('Failed to save note:', error);
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to save note'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentEditingNote) return;

    setIsDeleting(true);

    try {
      await deleteNote(currentEditingNote.id);
      handleClose();
    } catch (error) {
      logger.error('Failed to delete note:', error);
      // Error is already handled in the context and displayed in the UI
    } finally {
      setIsDeleting(false);
    }
  };

  const dialogFooter = (
    <div className='flex items-center justify-between px-6 py-4'>
      <div>
        {currentEditingNote && (
          <Button
            label='Delete'
            icon='pi pi-trash'
            onClick={handleDelete}
            className='!bg-red-600 !border-red-600 !text-white hover:!bg-red-700 hover:!border-red-700 disabled:!bg-red-400 disabled:!border-red-400'
            disabled={isSubmitting || isDeleting}
            loading={isDeleting}
          />
        )}
      </div>
      <div className='flex items-center gap-3'>
        <Button
          label='Cancel'
          icon='pi pi-times'
          onClick={handleClose}
          className='p-button-text'
          disabled={isSubmitting || isDeleting}
        />
        <Button
          label={currentEditingNote ? 'Update Note' : 'Create Note'}
          icon='pi pi-check'
          onClick={handleSubmit}
          disabled={
            !formData.toolName.trim() ||
            !formData.toolBody.trim() ||
            isSubmitting ||
            isDeleting
          }
          loading={isSubmitting}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      header={currentEditingNote ? 'Edit Note' : 'Create Note'}
      visible={isEditorModalOpen}
      style={{ width: '70vw', height: '70vh' }}
      onHide={handleClose}
      draggable={false}
      footer={dialogFooter}
      maximizable
      contentClassName='!p-6 !m-0'
      className='!border-border'
    >
      <div className='h-full flex flex-col space-y-6'>
        {/* Error Message */}
        {errorMessage && (
          <div className='bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
            {errorMessage}
          </div>
        )}

        {/* Form Fields */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label
              htmlFor='toolName'
              className='flex justify-between text-sm font-medium text-foreground dark:text-foreground-dark mb-2'
            >
              <span>Tool Name *</span>
              <span
                className={`text-xs ${
                  formData.toolName.length > MAX_TOOL_NAME_LENGTH
                    ? 'text-red-500'
                    : 'text-foreground dark:text-foreground-dark'
                }`}
              >
                {formData.toolName.length}/{MAX_TOOL_NAME_LENGTH}
              </span>
            </label>
            <input
              id='toolName'
              type='text'
              value={formData.toolName}
              maxLength={MAX_TOOL_NAME_LENGTH}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, toolName: e.target.value }));
                if (errorMessage) setErrorMessage(null);
              }}
              placeholder='Enter tool name...'
              className='w-full px-3 py-2 border border-input rounded-lg focus:outline-none dark:text-foreground-dark focus:ring-2 focus:ring-ring focus:border-transparent'
              autoFocus
            />
          </div>
          <div className='flex items-end'>
            <button
              type='button'
              onClick={() =>
                setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }
              className={`flex items-center gap-1.5 px-3 py-2 mb-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                formData.isActive
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200'
                  : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full ${
                  formData.isActive ? 'bg-emerald-500' : 'bg-slate-400'
                }`}
              />
              {formData.isActive ? 'Injected' : 'Not Injected'}
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor='toolDescription'
            className='flex justify-between text-sm font-medium mb-2 dark:text-foreground-dark'
          >
            <span>Description</span>
            <span
              className={`text-xs ${
                formData.toolDescription.length > MAX_TOOL_DESCRIPTION_LENGTH
                  ? 'text-red-500'
                  : 'text-foreground dark:text-foreground-dark'
              }`}
            >
              {formData.toolDescription.length}/{MAX_TOOL_DESCRIPTION_LENGTH}
            </span>
          </label>
          <textarea
            id='toolDescription'
            value={formData.toolDescription}
            maxLength={MAX_TOOL_DESCRIPTION_LENGTH}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                toolDescription: e.target.value,
              }))
            }
            placeholder='Describe what this tool does...'
            rows={2}
            className='w-full px-3 py-2 border border-input rounded-lg text-foreground dark:text-foreground-dark placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none'
          />
        </div>

        {/* Content Text Area */}
        <div className='flex-1 overflow-hidden flex flex-col'>
          <label
            htmlFor='toolBody'
            className='flex justify-between text-sm font-medium text-foreground mb-2'
          >
            <span>Content *</span>
            <span
              className={`text-xs ${
                formData.toolBody.length > MAX_TOOL_BODY_LENGTH
                  ? 'text-red-500'
                  : 'text-foreground dark:text-foreground-dark'
              }`}
            >
              {formData.toolBody.length}/{MAX_TOOL_BODY_LENGTH}
            </span>
          </label>
          <textarea
            id='toolBody'
            value={formData.toolBody}
            maxLength={MAX_TOOL_BODY_LENGTH}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, toolBody: e.target.value }))
            }
            placeholder='Write your note content here...'
            className='w-full flex-1 px-4 py-3 bg-background border border-input rounded-lg text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none leading-relaxed'
          />
        </div>
      </div>
    </Dialog>
  );
};

export default NoteEditorModal;
